import enums from '@/enums';

const routes = [
  {
    // =============================================================================
    // MAIN LAYOUT ROUTES
    // =============================================================================
    path: '',
    component: () => import('../layouts/main/Main.vue'),
    name: 'main-layout',
    meta: {
      requiresAuth: true,
    },
    redirect: '/home',
    children: [
      // =============================================================================
      // Theme Routes
      // =============================================================================
      {
        path: '/home',
        name: 'home',
        component: () => import('@/views/TheHome.vue'),
        meta: {
          pageTitle: '$General.Home',
        },
      },
      {
        path: '/analytics/campaigns',
        name: 'campaigns-analytics',
        component: () => import('../views/analytics/CampaignsAnalytics.vue'),
        meta: {
          pageTitle: '$PageTitle.CampaignsAnalytics',
        },
      },
      {
        path: '/analytics/contacts',
        name: 'contacts-analytics',
        component: () => import('../views/analytics/ContactsAnalytics.vue'),
        meta: {
          pageTitle: '$PageTitle.ContactsAnalytics',
        },
      },
      {
        path: '/analytics/reviews',
        name: 'reviews-analytics',
        component: () => import('../views/analytics/ReviewsAnalytics.vue'),
        meta: {
          pageTitle: '$PageTitle.ReviewsAnalytics',
        },
      },
      {
        path: '/analytics/feedback',
        name: 'feedback-analytics',
        component: () => import('../views/analytics/FeedbackAnalytics/FeedbackAnalytics.vue'),
        props: {
          hideActionsColumn: true,
          hideUpdatedAtColumn: true,
          createdAtHeaderName: '$General.DateAndTime',
        },
        meta: {
          pageTitle: '$PageTitle.FeedbackAnalytics',
          roles: [enums.Auth.Role.ADMIN],
        },
      },
      {
        path: '/notifications',
        name: 'notifications',
        component: () => import('../views/pages/Notifications.vue'),
        meta: {
          pageTitle: '$General.Notification',
        },
      },

      // =============================================================================
      // Modules Routes
      // =============================================================================
      // Merchant Module
      {
        path: '/merchants',
        name: 'merchants',
        component: () => import('@/views/modules/merchant/MerchantList.vue'),
        meta: {
          pageTitle: '$Entities.Merchant',
          operation: enums.Operation.LIST,
          roles: [enums.Auth.Role.ADMIN],
        },
      },

      // App Settings Module
      {
        path: '/app-settings',
        name: 'app-settings',
        component: () => import('@/views/modules/app-settings/TheAppSettings.vue'),
        meta: {
          pageTitle: '$Entities.AppSettings',
          operation: enums.Operation.LIST,
          roles: [enums.Auth.Role.ADMIN],
        },
      },

      // App Services
      {
        path: '/app-services',
        name: 'app-services',
        component: () => import('@/views/modules/app-service/AppServiceList.vue'),
        meta: {
          pageTitle: '$Entities.AppService',
          operation: enums.Operation.LIST,
          roles: [enums.Auth.Role.ADMIN],
        },
      },

      // SMS Campaigns Module
      {
        path: '/sms/campaigns',
        name: 'sms-campaigns-history',
        component: () => import('@/views/modules/campaigns/sms-campaign/SMSCampaignList.vue'),
        props: {
          statusFilter: {
            filterType: enums.AppFilterType.ENUM,
            type: enums.AppFilter.FilterType.Enum.Type.NONE,
            filter: [enums.Campaign.Status.DRAFT, enums.Campaign.Status.PENDING],
          },
          hideUpdatedAtColumn: true,
          listType: enums.Campaign.ListType.History,
        },
        meta: {
          pageTitle: '$SMSCampaignModule.SMSHistory',
          operation: enums.Operation.LIST,
        },
      },
      {
        path: '/sms/campaigns/drafts',
        name: 'sms-campaigns-drafts',
        component: () => import('@/views/modules/campaigns/sms-campaign/SMSCampaignList.vue'),
        props: {
          statusFilter: {
            filterType: enums.AppFilterType.TEXT,
            type: enums.AppFilter.FilterType.Text.Type.EQUALS,
            filter: enums.Campaign.Status.DRAFT,
          },
          listType: enums.Campaign.ListType.Drafts,
        },
        meta: {
          pageTitle: '$SMSCampaignModule.SMSDraft',
          operation: enums.Operation.LIST,
        },
      },
      {
        path: '/sms/campaigns/scheduled',
        name: 'sms-campaigns-scheduled',
        component: () => import('@/views/modules/campaigns/sms-campaign/SMSCampaignList.vue'),
        props: {
          statusFilter: {
            filterType: enums.AppFilterType.TEXT,
            type: enums.AppFilter.FilterType.Text.Type.EQUALS,
            filter: enums.Campaign.Status.PENDING,
          },
          listType: enums.Campaign.ListType.Scheduled,
        },
        meta: {
          pageTitle: '$SMSCampaignModule.SMSScheduled',
          operation: enums.Operation.LIST,
        },
      },

      {
        path: '/sms/campaigns/create',
        name: 'sms-campaigns-create',
        component: () => import('@/views/modules/campaigns/sms-campaign/SMSCampaignListCreateOrEdit.vue'),
        props: {
          entity: enums.Entity.SMS_CAMPAIGN,
          operation: enums.Operation.CREATE,
        },
        meta: {
          pageTitle: '$SMSCampaignModule.CreateAnSMSCampaign',
          operation: enums.Operation.CREATE,
        },
      },

      {
        path: '/sms/campaigns/resend',
        name: 'sms-campaigns-resend',
        component: () => import('@/views/modules/campaigns/sms-campaign/SMSCampaignListCreateOrEdit.vue'),
        props: {
          entity: enums.Entity.SMS_CAMPAIGN,
          operation: enums.Operation.CLONE,
        },
        meta: {
          pageTitle: '$SMSCampaignModule.ResendAnSMSCampaign',
          operation: enums.Operation.CLONE,
        },
      },

      {
        path: '/sms/campaigns/create-from-contacts',
        name: 'sms-campaigns-create-contacts',
        component: () => import('@/views/modules/campaigns/sms-campaign/SMSCampaignListCreateOrEdit.vue'),
        props: true,
        meta: {
          pageTitle: '$SMSCampaignModule.CreateAnSMSCampaign',
          operation: enums.Operation.CREATE,
        },
      },

      {
        path: '/sms/campaigns/edit',
        name: 'sms-campaigns-edit',
        component: () => import('@/views/modules/campaigns/sms-campaign/SMSCampaignListCreateOrEdit.vue'),
        props: {
          entity: enums.Entity.SMS_CAMPAIGN,
          operation: enums.Operation.EDIT,
        },
        meta: {
          pageTitle: '$SMSCampaignModule.EditSMSCampaign',
          operation: enums.Operation.EDIT,
        },
      },
      {
        path: '/sms/campaigns/quick',
        name: 'sms-campaigns-quick',
        component: () => import('@/views/modules/campaigns/sms-campaign/SMSCampaignListQuickCreateOrEdit.vue'),
        props: {
          entity: enums.Entity.SMS_CAMPAIGN,
          operation: enums.Operation.CREATE,
        },
        meta: {
          pageTitle: '$SMSCampaignModule.QuickSMSCampaignPageTitle',
        },
      },

      // SMS Inbounds Module
      {
        path: '/sms/inbox',
        name: 'sms-inbounds',
        component: () => import('@/views/modules/inbound-sms/InboundSMSList.vue'),
        props: {
          hideUpdatedAtColumn: true,
          createdAtHeaderName: '$General.DateAndTime',
        },
        meta: {
          pageTitle: '$Entities.SMSInbound',
          operation: enums.Operation.LIST,

        },
      },

      // Reviews Module
      {
        path: '/reviews',
        name: 'reviews',
        component: () => import('@/views/modules/reviews/ReviewsSettings.vue'),
        props: {
          hideActionsColumn: true,
          hideCreatedAtColumn: true,
          updatedAtHeaderName: '$General.Date',
        },
        meta: {
          pageTitle: '$General.Review',
          operation: enums.Operation.LIST,

        },
      },
      {
        path: '/reviews/public-form',
        name: 'reviews-public-form',
        component: () => import('@/views/modules/reviews/ReviewsSettings.vue'),
        props: {
          hideActionsColumn: true,
          hideCreatedAtColumn: true,
          updatedAtHeaderName: '$General.Date',
        },
        meta: {
          pageTitle: '$General.Review',
          operation: enums.Operation.LIST,
          breadcrumb: [
            { title: '$General.Review', url: '/reviews' },
            { title: 'Public form', active: true },
          ],
        },
      },

      // Waitlist Module
      {
        path: '/waitlist',
        name: 'waitlist',
        component: () => import('@/views/modules/waiting-list/WaitingList.vue'),
        meta: {
          pageTitle: '$General.WaitingList',
          operation: enums.Operation.LIST,
        },
        props: {
          hideTimestampsColumns: true,
          hideActionsColumn: true,
          floatingFilter: false,
        },
      },
      {
        path: '/waitlist/public-form',
        name: 'waitlist-public-form',
        component: () => import('@/views/modules/waiting-list/WaitingList.vue'),
        meta: {
          pageTitle: '$General.WaitingList',
          operation: enums.Operation.LIST,
          breadcrumb: [
            { title: '$General.WaitingList', url: '/waitlist' },
            { title: 'Public form', active: true },
          ],
        },
        props: {
          hideTimestampsColumns: true,
          hideActionsColumn: true,
          floatingFilter: false,
        },
      },
      {
        path: '/waitlist/configuration',
        name: 'waitlist-configuration',
        component: () => import('@/views/modules/waiting-list/WaitingList.vue'),
        meta: {
          pageTitle: '$PageTitle.WaitlistConfiguration',
          operation: enums.Operation.LIST,
          breadcrumb: [
            { title: '$General.WaitingList', url: '/waitlist' },
            { title: 'Waitlist configuration', active: true },
          ],
        },
        props: {
          hideTimestampsColumns: true,
          hideActionsColumn: true,
          floatingFilter: false,
        },
      },
      // Redirect old waiting-list URL to new waitlist URL
      {
        path: '/waiting-list',
        redirect: '/waitlist',
      },

      {
        path: '/contacts/attributes',
        name: 'custom-fields',
        component: () => import('@/views/modules/attribute/AttributeList.vue'),
        meta: {
          pageTitle: '$Entities.ContactAttribute',
          operation: enums.Operation.LIST,

        },
      },

      {
        path: '/contacts/tags',
        name: 'tags',
        component: () => import('@/views/modules/tags/TagList.vue'),
        meta: {
          pageTitle: '$Entities.Tag',
          operation: enums.Operation.LIST,

        },
      },

      // Contacts Module
      {
        path: '/contacts',
        name: 'contacts',
        component: () => import('@/views/modules/contacts/ContactList.vue'),
        meta: {
          pageTitle: '$Entities.Contact',
          operation: enums.Operation.LIST,

        },
      },
      {
        path: '/contacts/import',
        name: 'contacts-import',
        component: () => import('@/views/modules/contacts/ContactList.vue'),
        props: {
          startImport: true,
        },
        meta: {
          pageTitle: 'Import contacts',
          operation: enums.Operation.LIST,
          breadcrumb: [
            {
              title: '$Entities.Contact',
              i18n: 2,
              fullPath: '/contacts',
            },
            {
              title: 'Import contacts',
              i18n: 1,
              fullPath: '/contacts/import',
            },
          ],
        },
      },

      {
        path: '/trash/contacts',
        name: 'contacts-trash',
        component: () => import('@/views/modules/contacts/ContactList.vue'),
        props: {
          trash: true,
        },
        meta: {
          pageTitle: '$PageTitle.DeletedContacts',
          operation: enums.Operation.LIST,

        },
      },
      {
        path: '/contacts/segments',
        name: 'segments',
        component: () => import('@/views/modules/segment/SegmentList.vue'),
        meta: {
          pageTitle: '$Entities.Segment',
          operation: enums.Operation.LIST,

        },
      },

      // SMS Templates Module
      {
        path: '/sms/templates/test',
        name: 'sms-templates-tab',
        component: () => import('@/views/modules/templates/sms-template/SMSTemplateTest.vue'),
        meta: {
          pageTitle: '$Entities.SMSTemplate',
          operation: enums.Operation.LIST,

        },
      },
      {
        path: '/sms/templates',
        name: 'sms-templates',
        component: () => import('@/views/modules/templates/sms-template/SMSTemplateList.vue'),
        props: { actionsMinWidth: 200 },
        meta: {
          pageTitle: '$Entities.SMSTemplate',
          operation: enums.Operation.LIST,

        },
      },
      {
        path: '/trash/sms-templates',
        name: 'sms-templates-trash',
        component: () => import('@/views/modules/templates/sms-template/SMSTemplateList.vue'),
        props: { trash: true, actionsMinWidth: 150 },
        meta: {
          pageTitle: '$PageTitle.DeletedSMSTemplates',
          operation: enums.Operation.LIST,

        },
      },

      // Caller Ids Module
      {
        path: '/sms/dedicated-numbers',
        name: 'caller-ids',
        component: () => import('@/views/modules/sender-ids/caller-id/CallerIdList.vue'),
        meta: {
          pageTitle: '$Entities.CallerID',
          operation: enums.Operation.LIST,

        },
      },

      // Caller Ids Module
      {
        path: '/integrations/square',
        name: 'square-integration',
        component: () => import('@/views/modules/square/SquareIntegrationConfig.vue'),
        meta: {
          pageTitle: '$Integrations.Square',
          operation: enums.Operation.LIST,

        },
      },

      // =============================================================================
      // User settings responsive menu
      // =============================================================================
      {
        path: '/settings',
        name: 'user-settings',
        component: () => import('@/views/modules/user/user-settings-menu/UserSettings.vue'),
        meta: {
          pageTitle: '$General.Setting',
          i18nPageTitle: 2,
          breadcrumb: [
            { title: '$General.Setting', url: '/settings' },
          ],
        },
      },


      {
        path: '/settings/account-details',
        name: 'user-account-details',
        component: () => import('@/views/modules/user/user-account/user-account-details/UserAccountDetails.vue'),
        meta: {
          pageTitle: '$UserSettings.$Account.$AccountDetails.TabTitle',
          breadcrumb: [
            { title: '$General.Setting', url: '/settings' },
            { title: '$UserSettings.$Account.$AccountDetails.TabTitle', active: true },
          ],
        },
      },
      {
        path: '/settings/branding',
        name: 'user-account-branding',
        component: () => import('@/views/modules/user/user-account/user-account-branding/UserAccountBranding.vue'),
        meta: {
          pageTitle: '$UserSettings.$Account.$Branding.TabTitle',
          breadcrumb: [
            { title: '$General.Setting', url: '/settings' },
            { title: '$UserSettings.$Account.$Branding.TabTitle', active: true },
          ],
        },
      },
      {
        path: '/settings/company-policies',
        name: 'user-account-policies',
        component: () => import('@/views/modules/user/user-account/user-account-company-policies/UserAccountCompanyPolicies.vue'),
        meta: {
          pageTitle: '$UserSettings.$Account.$CompanyPolicies.TabTitle',
          breadcrumb: [
            { title: '$General.Setting', url: '/settings' },
            { title: '$UserSettings.$Account.$CompanyPolicies.TabTitle', active: true },
          ],
        },
      },
      {
        path: '/settings/opening-hours',
        name: 'user-account-opening-hours',
        component: () => import('@/views/modules/user/user-account/user-account-opening-hours/UserAccountOpeningHours.vue'),
        meta: {
          pageTitle: '$UserSettings.$Account.$OpeningHours.TabTitle',
          breadcrumb: [
            { title: '$General.Setting', url: '/settings' },
            { title: '$UserSettings.$Account.$OpeningHours.TabTitle', active: true },
          ],
        },
      },
      {
        path: '/settings/security',
        name: 'user-account-security',
        component: () => import('@/views/modules/user/user-account/user-account-security/UserAccountSecurity.vue'),
        meta: {
          pageTitle: '$UserSettings.$Account.$Security.TabTitle',
          breadcrumb: [
            { title: '$General.Setting', url: '/settings' },
            { title: '$UserSettings.$Account.$Security.TabTitle', active: true },
          ],
        },
      },


      {
        path: '/settings/payment-methods',
        name: 'user-account-billing-payment-methods',
        component: () => import('@/views/modules/user/user-billing/UserAccountBillingPaymentMethods.vue'),
        meta: {
          pageTitle: '$UserSettings.$Billing.$PaymentMethods.TabTitle',
          breadcrumb: [
            { title: '$General.Setting', url: '/settings' },
            { title: '$UserSettings.$Billing.$PaymentMethods.TabTitle', active: true },
          ],
        },
      },
      {
        path: '/settings/billing-history',
        name: 'user-account-billing-history',
        component: () => import('@/views/modules/user/user-billing/user-billing-history/UserAccountBillingHistory.vue'),
        props: {
          hideActionsColumn: true,
          hideTimestampsColumns: true,
        },
        meta: {
          pageTitle: '$UserSettings.$Billing.$History.TabTitle',
          breadcrumb: [
            { title: '$General.Setting', url: '/settings' },
            { title: '$UserSettings.$Billing.$History.TabTitle', active: true },
          ],
        },

      },


      {
        path: '/settings/prepaid-packages',
        name: 'user-account-purchases-overview-my-prepaid-packages',
        component: () => import('@/views/modules/user/user-purchases/user-my-purchases/UserMyPurchasesPrepaidPackages.vue'),
        props: {
          payPeriod: enums.StripeEnums.Checkout.Mode.PAYMENT,
        },
        meta: {
          pageTitle: '$UserSettings.$Shop.$MyPurchases.$PrepaidPackages.TabTitle',
          breadcrumb: [
            { title: '$General.Setting', url: '/settings' },
            { title: '$UserSettings.$Shop.$MyPurchases.$PrepaidPackages.TabTitle', active: true },
          ],
        },
      },
      {
        path: '/settings/dedicated-numbers',
        name: 'user-account-purchases-overview-my-dedicated-numbers',
        component: () => import('@/views/modules/user/user-purchases/user-my-purchases/my-dedicated-numbers/UserMyPurchasesDedicatedNumbers.vue'),
        meta: {
          pageTitle: '$UserSettings.$Shop.$MyPurchases.$DedicatedNumbers.TabTitle',
          breadcrumb: [
            { title: '$General.Setting', url: '/settings' },
            { title: '$UserSettings.$Shop.$MyPurchases.$DedicatedNumbers.TabTitle', active: true },
          ],
        },
      },
      // Shop-related routes are now flattened under /settings
      {
        path: '/settings/auto-top-up',
        name: 'user-account-purchases-auto-top-up-account',
        component: () => import('@/views/modules/user/user-purchases/user-purchases-top-ups/UserAccountPurchasesAutoTopUpAccount.vue'),
        meta: {
          pageTitle: '$UserSettings.$Shop.$ManageTopUps.$AutoTopUpAccount.TabTitle',
          breadcrumb: [
            { title: '$General.Setting', url: '/settings' },
            { title: '$UserSettings.$Shop.$ManageTopUps.$AutoTopUpAccount.TabTitle', active: true },
          ],
        },
      },

      // =============================================================================
      // Pages Routes
      // =============================================================================
      {
        path: '/faqs',
        name: 'faq',
        component: () => import('@/views/pages/Faq.vue'),
        meta: {
          pageTitle: '$General.FAQ',
        },
      },
      {
        path: '/documentation',
        name: 'documentation',
        component: () => import('@/views/pages/KnowledgeBase.vue'),
        meta: {
          pageTitle: '$General.Documentation',
        },
      },
      {
        path: '/documentation/:category',
        name: 'documentation-category',
        component: () => import('@/views/pages/KnowledgeBaseCategory.vue'),
        meta: {
          pageTitle: '$General.Documentation',
          parent: 'documentation',
        },
      },
      {
        path: '/documentation/:category/:question',
        name: 'documentation-category-question',
        component: () => import('@/views/pages/KnowledgeBaseCategoryQuestion.vue'),
        meta: {
          pageTitle: '$General.Documentation',
          parent: 'documentation',
        },
      },
      {
        path: '/pages/search',
        name: 'page-search',
        component: () => import('@/views/pages/Search.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'Pages' },
            { title: 'Search', active: true },
          ],
          pageTitle: '$PageTitle.Search',
        },
      },
      {
        path: '/pages/invoice',
        name: 'page-invoice',
        component: () => import('@/views/pages/Invoice.vue'),
        meta: {
          breadcrumb: [
            { title: 'Home', url: '/' },
            { title: 'Pages' },
            { title: 'Invoice', active: true },
          ],
          pageTitle: '$PageTitle.Invoice',
        },
      },
    ],
  },
  // =============================================================================
  // FULL PAGE LAYOUTS
  // =============================================================================
  {
    path: '/',
    component: () => import('@/layouts/full-page/FullPage.vue'),
    children: [
      // =============================================================================
      // PAGES
      // =============================================================================
      {
        path: '/accept-terms-and-conditions',
        component: () => import('@/views/pages/auth/TheAcceptTermsAndConditions.vue'),
        name: 'accept-terms-and-conditions',
        meta: {
          requiresAuth: true,
          isTermsAndConditions: true,
        },
      },
      {
        path: '/callback',
        name: 'auth-callback',
        component: () => import('@/views/Callback.vue'),
        meta: {
        },
      },
      {
        path: '/login',
        alias: [
          '/login.html',
        ],
        name: 'login',
        component: () => import('@/views/pages/auth/TheAuth.vue'),
        props: { operation: 'Login' },
        meta: {
          requiresGuest: true,
        },
      },
      {
        path: '/log-in-second-step',
        name: 'login-second-step',
        component: () => import('@/views/pages/auth/TheAuthSecondStep.vue'),
      },
      {
        path: '/sign-up',
        alias: [
          '/signup.html',
        ],
        name: 'register',
        component: () => import('@/views/pages/auth/TheAuth.vue'),
        props: { operation: 'SignUp' },
        meta: {
          requiresGuest: true,
        },
      },
      {
        path: '/forgot-password',
        alias: [
          '/forgot-password.html',
        ],
        name: 'forgot-password',
        component: () => import('@/views/pages/auth/TheForgotPassword.vue'),
      },
      {
        path: '/reset-password/:tokenRecoverPassword',
        name: 'reset-password',
        component: () => import('@/views/pages/auth/TheResetPassword.vue'),
      },
      {
        path: '/invited-sign-up/:email/:securityToken',
        name: 'invited-sign-up',
        component: () => import('@/views/pages/auth/TheInviteRegister.vue'),
        meta: {
          requiresGuest: true,
        },
      },
      {
        path: '/not-authorised',
        name: 'not-authorized',
        component: () => import('@/views/pages/auth/TheNotAuthorized.vue'),
        meta: {
        },
      },
      {
        path: '/pages/lock-screen',
        name: 'page-lock-screen',
        component: () => import('@/views/pages/LockScreen.vue'),
        meta: {
        },
      },
      {
        path: '/pages/comingsoon',
        name: 'page-coming-soon',
        component: () => import('@/views/pages/ComingSoon.vue'),
        meta: {
        },
      },
      {
        path: '/notification-preferences/:uuid',
        name: 'notification-preferences',
        component: () => import('@/views/pages/NotificationPreferences.vue'),
      },
      {
        path: '/contact/opt-out/:contactId/:optOutToken',
        name: 'contact-opt-out',
        component: () => import('@/views/pages/ContactOptOut.vue'),
      },
      {
        path: '/email-verification/:email/:verificationToken',
        name: 'email-verification',
        component: () => import('@/views/pages/auth/TheAuthEmailVerification.vue'),
      },
      {
        path: '/waitlist/:tenantUrlLinksSuffix/:waitingListId?',
        name: 'waitlist-form',
        component: () => import('@/views/pages/HostedWaitingListForm.vue'),
        meta: {
          hostedPublicPage: true,
        },
      },
      // Redirect old waiting-list public form URL to new waitlist URL
      {
        path: '/waiting-list/:tenantUrlLinksSuffix/:waitingListId?',
        redirect: to => `/waitlist/${to.params.tenantUrlLinksSuffix}${to.params.waitingListId ? '/' + to.params.waitingListId : ''}`,
      },
      {
        path: '/review/:tenantUrlLinksSuffix/:contactToken?',
        name: 'review-form',
        component: () => import('@/views/pages/HostedInternalReviewForm.vue'),
        meta: {
          hostedPublicPage: true,
        },
      },
      {
        path: '/privacy-policy/:tenantUrlLinksSuffix',
        name: 'hosted-privacy-policy',
        component: () => import('@/views/pages/HostedPrivacyPolicy.vue'),
        meta: {
          hostedPublicPage: true,
        },
      },
      {
        path: '/page-not-found',
        name: 'page-error-404',
        component: () => import('@/views/pages/Error404.vue'),
      },
      {
        path: '/pages/error-500',
        name: 'page-error-500',
        component: () => import('@/views/pages/Error500.vue'),
        meta: {
        },
      },
      {
        path: '/pages/maintenance',
        name: 'page-maintenance',
        component: () => import('@/views/pages/Maintenance.vue'),
        meta: {
        },
      },
    ],
  },
  // Render 404 page directly, if no match found
  {
    path: '*',
    component: () => import('@/views/pages/Error404.vue'),
  },
];

export default routes;
